/**
 * What a Required-action row OPENS, and how a page of the queue is taken apart
 * to find one.
 *
 * Split from ./helpers — which owns how a row LOOKS (columns, copy, badges,
 * formatters) — because a row can now be opened two ways: by pressing its
 * Action button, and by an `rqOpen` deep link that never renders that button
 * at all (./url-state, ./useRowDeepLink). Two builders would drift, and the
 * drift would be invisible: both open the same modal, just with subtly
 * different targets.
 *
 * React-free and axios-free, like ./helpers: the row shapes and
 * `AdjustPaymentTarget` are `import type` only, so they erase at compile time.
 */

import dayjs from 'dayjs'

import type {
  ReconIssueRow,
  ReviewQueueItem,
  ReviewTransaction,
} from '../../../api/reconciliation-v2-api-client'
import type {
  AdjustPaymentTarget,
  AdjustSectionKey,
} from '../AdjustPaymentModal.types'
import {
  discrepancyBenefitLabel,
  insuredOrEmployeeName,
  REASON_ACTION_LABEL,
} from './helpers'
import type { ReviewQueueOpenTarget } from './url-state'

/**
 * The rails that carry a lookup reference: the two card rails. Marqeta sends
 * `transaction_token`, WEX its merchant log id; EPS COALESCEs the pair into the
 * one `token` field.
 */
const TOKEN_SOURCES: ReadonlySet<string> = new Set(['marqeta', 'wex'])

/**
 * A transaction row's Token cell — null for every rail that has no token, so
 * the cell renders a dash.
 *
 * Gated on `source` as well as on `token` being set, deliberately: the cell
 * promises a CARD token, and a value arriving under `token` from some future
 * rail would be something else wearing the name. The internal transaction id
 * is NOT a fallback here — it reads exactly like a token and is not one, which
 * is the confusion this column was renamed to end.
 *
 * WEX's half is a CARD id and so repeats down the column for one card's rows.
 * Not a bug to be deduplicated: it is the id the WEX routes take, and the
 * per-transaction alternative can be looked up nowhere.
 */
export const transactionToken = (row: ReviewTransaction): string | null =>
  TOKEN_SOURCES.has(row.source) ? row.token : null

/**
 * The `conf` on a transaction row's modal target. Keeps the fallback the Token
 * CELL drops: the modal's rail has a single reference line and no column
 * header to qualify it, so a transaction id there beats an empty slot. Matches
 * TransactionDetailsPanel, which fills the same line the same way.
 */
export const transactionReference = (row: ReviewTransaction): string =>
  row.token ?? row.transactionId

/**
 * The Adjust Payment target for an unposted-transaction row — an `unassigned`
 * target, since the transaction has not landed on any benefit yet.
 */
export const buildTransactionTarget = (
  row: ReviewTransaction,
  initialSection?: AdjustSectionKey
): AdjustPaymentTarget => {
  const name = insuredOrEmployeeName(row)
  return {
    kind: 'unassigned',
    initialSection,
    transactionId: row.transactionId,
    transactionNumber: row.transactionNumber,
    amount: row.amount,
    carrier: row.carrierName,
    // Deliberately a different format from the Date CELL ('MMM DD, YYYY h:mm
    // A'): the modal's rail shows a day, not a timestamp.
    date: dayjs(row.transactionDate).format('MMM D, YYYY'),
    conf: transactionReference(row),
    employeePublicKey: row.employeePublicKey,
    source: row.source,
    // The very lookup that renders the row's button, so the success toast
    // cannot come back with a different verb.
    promisedAction: REASON_ACTION_LABEL[row.reason],
    transactionStatus: row.transactionStatus,
    transactionType: row.transactionType,
    // Seed the picker with the resolved benefit for any row that has one
    // (refund, auto-allocate-failed, pending-allocation). Only "unresolved
    // benefit" rows have no benefitId, so they stay empty.
    prefillBenefit: row.benefitId
      ? { benefitId: row.benefitId, label: name ?? row.benefitId }
      : undefined,
  }
}

/**
 * The Adjust Payment target for a discrepancy row — a CELL target: the work
 * already sits in a benefit-month, which the modal fetches the detail for.
 *
 * Note what it does NOT carry: the issue type. Every issue on one
 * benefit-month opens the same cell, which is why the third part of an
 * `rqOpen` composite changes which ROW is matched but never the target built
 * from it.
 */
export const buildDiscrepancyTarget = (
  issue: ReconIssueRow
): AdjustPaymentTarget => ({
  benefitId: issue.benefitId,
  periodStartDate: issue.periodStartDate,
  // Keeps the "· Carrier" suffix the name cell drops: the modal pins this on
  // its benefit picker and has no Carrier of its own.
  benefitLabel: discrepancyBenefitLabel(issue),
  employeePublicKey: issue.employeePublicKey,
  insuredPersons: issue.insuredPersons,
  carrierName: issue.carrierName,
  // This tab's button says Allocate, so the toast must not say
  // "Reallocated". It cannot promise the VERB outright: a refund inside this
  // cell is still Posted, and the row cannot see which transaction the
  // operator will pick.
  fromRequiredActionQueue: true,
})

/**
 * The unposted transactions on a page of the queue. `kind === 'transaction'`
 * here — see {@link discrepanciesOf} for the arm whose discriminator differs
 * from its query param.
 */
export const transactionsOf = (
  items: readonly ReviewQueueItem[]
): ReviewTransaction[] =>
  items.flatMap((item) =>
    item.kind === 'transaction' && item.transaction ? [item.transaction] : []
  )

/**
 * The discrepancies on a page of the queue.
 *
 * `kind === 'issue'`, NOT 'discrepancy': the request param uses the
 * operator-facing word while the ROW discriminator keeps its original wire
 * value. Renaming either is breaking on the EPS side; do not conflate them.
 */
export const discrepanciesOf = (
  items: readonly ReviewQueueItem[]
): ReconIssueRow[] =>
  items.flatMap((item) =>
    item.kind === 'issue' && item.issue ? [item.issue] : []
  )

/**
 * React key for a discrepancy row.
 *
 * All three parts, for the same reason an `rqOpen` composite needs all three:
 * one benefit-month can carry both an auto (overpaid / underpaid) and a
 * `manual_review` issue as two separate rows, so benefitId + period alone
 * collides and orphans a node. The separator is private to this key — the URL
 * composite has its own, and {@link resolveOpenTarget} matches on the fields
 * rather than on either encoding.
 */
export const discrepancyKey = (issue: ReconIssueRow): string =>
  `${issue.benefitId}|${issue.periodStartDate}|${issue.issueType}`

/**
 * The row a deep link points at, as a target the modal can open — or null when
 * it is not on the page the panel fetched.
 *
 * Not found is an ordinary outcome, not an error: the link may point at work a
 * colleague has already cleared, and the search that narrows the page matches
 * the benefit id but neither the period nor the issue type.
 */
export const resolveOpenTarget = (
  items: readonly ReviewQueueItem[],
  open: ReviewQueueOpenTarget
): AdjustPaymentTarget | null => {
  if (open.kind === 'transaction') {
    const row = transactionsOf(items).find(
      (transaction) => transaction.transactionId === open.transactionId
    )
    return row ? buildTransactionTarget(row) : null
  }
  const row = discrepanciesOf(items).find(
    (issue) =>
      issue.benefitId === open.benefitId &&
      issue.periodStartDate === open.periodStartDate &&
      issue.issueType === open.issueType
  )
  return row ? buildDiscrepancyTarget(row) : null
}
